import React, { useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { useLeaflet } from 'react-leaflet';
import L, { divIcon } from 'leaflet';

import Popup from '../Popup';
import { mapPadding } from '../helpers';

import "leaflet.markercluster/dist/leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import './style.scss';
import MapContext from '../../../../containers/Map/MapContext';

const mcg = L.markerClusterGroup({
  zoomToBoundsOnClick: false,
  showCoverageOnHover: false,
  iconCreateFunction: (cluster) => {
    let size = "tiny";
    if (cluster.getChildCount() > 100) {
      size = "huge";
    } else if (cluster.getChildCount() > 50) {
      size = "large";
    } else if (cluster.getChildCount() > 25) {
      size = "normal";
    } else if (cluster.getChildCount() > 10) {
      size = "medium";
    }

    return divIcon({
      className: `MarkerCluster MarkerCluster--${size}`,
      html: `<div><span>${cluster.getChildCount()}</span></div>`,
    });
  },
});

const icon = divIcon({
  className: 'Icon',
  html: `<span class="icon icon-flag icon-variant-0"></span>`,
});

const iconActive = divIcon({
  className: 'Icon',
  html: `<span class="icon icon-flag icon-variant-1"></span>`,
});

const iconFull = divIcon({
  className: 'Icon',
  html: `<span class="icon icon-flag icon-variant-3"></span>`,
});

const iconFullActive = divIcon({
  className: 'Icon',
  html: `<span class="icon icon-flag icon-variant-4"></span>`,
});

// resets all markers on the map and make the given one active
const makeMarkerActive = (map, marker) => {
  const latlng = marker.getLatLng();
  const offset = mapPadding();
  const popupHeight = 300;
  const x = map.latLngToContainerPoint(latlng).x - offset[0] / 2;
  const y = map.latLngToContainerPoint(latlng).y - ((offset[1] + popupHeight) / 2);
  const point = map.containerPointToLatLng([x, y]);
  map.setView(point, map._zoom);
};

// Process all items in the array and place them on the map
const renderMarkers = (results, map) => {
  const markers = [];

  results.forEach((item) => {
    if (item.getIn(['geo_location', 'lat'], null) !== null && item.getIn(['geo_location', 'lon'], null) !== null) {
      const point = L.marker(new L.LatLng(item.getIn(['geo_location', 'lat']), item.getIn(['geo_location', 'lon'])), {
        icon: item.getIn(['field_project_full', 0]) ? iconFull : icon,
        id: item.get('id'),
        title: item.getIn(['title', 0]),
        field_project_full: item.getIn(['field_project_full', 0]),
      });

      const popup = L.popup({
        autoPan: false,
        closeButton: false,
      })
        .setContent(
          () => {
            const div = document.createElement('div');
            ReactDOM.render(<Popup project={item} />, div);
            return div;
          }
        );

      point.bindPopup(popup);

      point.on('click', (e) => {
        makeMarkerActive(map, point);
        e.target.openPopup();
      });

      markers.push(point);
    }
  });

  mcg.clearLayers();
  mcg.addLayers(markers);
};

const MarkerCluster = ({ markers }) => {
  const { map } = useLeaflet();
  const { selectedProject } = useContext(MapContext);

  if (map) {
    mcg.getLayers().forEach((layer) => {
      if (layer.options.id === selectedProject) {
        layer.setIcon(layer.options.field_project_full ? iconFullActive : iconActive);
      } else {
        layer.setIcon(layer.options.field_project_full ? iconFull : icon);
      }
    });
  }

  useEffect(() => {
    mcg.on('clusterclick', (e) => {
      const ne = e.layer.getBounds().getNorthEast();
      const sw = e.layer.getBounds().getSouthWest();

      if (ne.equals(sw)) {
        e.layer.spiderfy();
      } else {
        map.flyToBounds(e.layer.getBounds(), { paddingTopLeft: mapPadding(), duration: 0.5 });
      }
    });
    map.addLayer(mcg);
    mcg.clearLayers();
  }, [map]);

  useEffect(() => {
    renderMarkers(markers, map);
  }, [markers, map]);

  return null;
};

export default MarkerCluster;
